import { Box, CircularProgress, Typography } from '@mui/material';
import { PageContainer } from '@toolpad/core/PageContainer';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import PartnersTable, { type PartnerWithRelationship } from '../components/Partners/PartnersTable';
import { useAccount } from '../contexts/AccountContext';
import { getAllPartnerRelationships, getAllPartners } from '../services/partnerService';

const PartnersPage: React.FC = () => {
  const { account } = useAccount();

  const [partners, setPartners] = useState<PartnerWithRelationship[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<React.ReactNode>('');

  const fetchPartners = useCallback(async () => {
    if (!account?.clientId) {
      setError('User not authenticated or missing client ID.');
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError('');

    try {
      // Fetch all visible partners
      const allPartners = await getAllPartners();

      // Fetch relationships for current client
      const relationshipsMap = await getAllPartnerRelationships(account.clientId);

      // Merge data
      const mergedPartners: PartnerWithRelationship[] = allPartners
        .filter((p) => p.isVisible !== false)
        .map(
          (p) =>
            ({
              ...p,
              relationshipStatus: relationshipsMap.get((p.id || p.clientId) as string) || null,
            }) as unknown as PartnerWithRelationship
        );

      setPartners(mergedPartners);
    } catch (err: unknown) {
      const e = err as Error;
      if (
        e.message.includes('Missing or insufficient permissions') ||
        e.message.includes('permission-denied')
      ) {
        setError(
          <>
            The partner features are not active on this account. Please contact{' '}
            <a
              href="mailto:sales@bidlogiq.ai"
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              sales@bidlogiq.ai
            </a>{' '}
            to enable this feature.
          </>
        );
      } else {
        setError(`Failed to load partners: ${e.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  }, [account]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  let content: React.ReactNode;
  if (isLoading && partners.length === 0) {
    content = (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading partners...</Typography>
      </Box>
    );
  } else if (error) {
    content = (
      <Typography color="error" sx={{ my: 2, textAlign: 'center' }}>
        {error}
      </Typography>
    );
  } else if (partners.length === 0 && !isLoading) {
    content = (
      <Typography sx={{ my: 4, textAlign: 'center', color: 'text.primary' }}>
        No partners currently available.
      </Typography>
    );
  } else {
    content = (
      <PartnersTable
        partners={partners}
        currentClientId={account?.clientId || null}
        isLoading={isLoading}
      />
    );
  }

  return (
    <PageContainer
      data-testid="partners-page"
      title="Subcontractors & JV Partners"
      maxWidth={false}
    >
      <Typography variant="body1" sx={{ mb: 3 }}>
        Browse and discover potential subcontractors and joint venture partners. You can pre-approve
        partners to signal your interest or pre-reject them to hide them from your standard view.
      </Typography>
      <Box>{content}</Box>
    </PageContainer>
  );
};

export default PartnersPage;
